import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast, ToastContainer } from 'react-toastify';
import { editUserAddress, getReturnMailReason } from '../api/Network';
import FormInput from '../components/FormsInput/FormInput';
import { FormSelect } from '../components/FormsInput/FormSelect';
import { UserAddress } from '../types/UserAddress';
import { CreateRecordFormData } from './CreateRecordModal';

type Props = {
  selectedRecord: UserAddress | null;
  onClose?: () => void;
};

export const UpdateRecordModal = ({ selectedRecord, onClose }: Props) => {
  const queryClient = useQueryClient();

  const { data: reasons } = useQuery(['reasons'], () => getReturnMailReason(), {
    onError: (error: Error) => toast.error(error.message),
    refetchOnWindowFocus: false,
    refetchInterval: 3600000,
  });
  const [isEditable, setIsEditable] = useState(false);
  useEffect(() => {
    if (selectedRecord?.createdBy.toLocaleLowerCase() === 'portal') {
      setIsEditable(true);
      console.log(isEditable, 'boolean');
      console.log(selectedRecord.createdBy, 'created by');
    } else {
      setIsEditable(false);
    }
  }, [selectedRecord?.createdBy]);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isValid },
  } = useForm<CreateRecordFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      companyName: null,
      addressLine1: '',
      addressLine2: null,
      city: '',
      state: null,
      zip: '',
      country: '',
      documentType: null,
      fileName: null,
      mailed: false,
      mailedDate: null,
      logicAppLastRunId: null,
      intelligentMailBarcodeDigits: null,
      isProcessed: false,
      reason: null,
      returnDate: null,
      returnReason: null,
      isDuplicate: false,
      deliveryAddress: null,
      deliveryCity: null,
      deliveryState: null,
      deliveryZip: null,
      alternateAddress: null,
      createdByApimUsername: '',
      modifiedByApimUsername: null,
    },
    mode: 'onTouched',
  });

  useEffect(() => {
    if (selectedRecord) {
      const normalizeEmptyToNull = (value: any) => (value === '' ? null : value);

      reset({
        firstName: selectedRecord.firstName ?? '',
        lastName: selectedRecord.lastName ?? '',
        companyName: selectedRecord.companyName ?? '',
        addressLine1: selectedRecord.addressLine1 ?? '',
        addressLine2: selectedRecord.addressLine2 ?? '',
        city: selectedRecord.city ?? '',
        state: selectedRecord.state ?? '',
        zip: String(selectedRecord.zip) ?? '',
        country: selectedRecord.country ?? '',
        documentType: selectedRecord.documentType ?? '',
        fileName: selectedRecord.fileName ?? '',
        mailed: selectedRecord.mailed ?? false,
        mailedDate: normalizeEmptyToNull(selectedRecord.mailedDate),
        logicAppLastRunId: selectedRecord.logicAppLastRunId ?? '',
        intelligentMailBarcodeDigits: normalizeEmptyToNull(selectedRecord.intelligentMailBarcodeDigits),
        isProcessed: selectedRecord.isProcessed ?? false,
        reason: normalizeEmptyToNull(selectedRecord.reason),
        returnDate: normalizeEmptyToNull(selectedRecord.returnDate),
        returnReason: normalizeEmptyToNull(selectedRecord.returnReason),
        isDuplicate: selectedRecord.isDuplicate || false,
        deliveryAddress: normalizeEmptyToNull(selectedRecord.deliveryAddress),
        deliveryCity: normalizeEmptyToNull(selectedRecord.deliveryCity),
        deliveryState: normalizeEmptyToNull(selectedRecord.deliveryState),
        deliveryZip: normalizeEmptyToNull(String(selectedRecord.deliveryZip)),
        alternateAddress: normalizeEmptyToNull(selectedRecord.alternateAddress),
        createdByApimUsername: selectedRecord.createdByApimUsername || '',
        modifiedByApimUsername: normalizeEmptyToNull(selectedRecord.modifiedByApimUsername),
      });
    }
  }, [selectedRecord?.id, reset]);

  const closeModal = (e?: React.MouseEvent<HTMLButtonElement | HTMLFormElement>) => {
    e?.preventDefault();
    (document.getElementById('edit_record_modal') as HTMLFormElement)?.close();
    reset();
    onClose?.();
  };

  const onSubmit = async (data: CreateRecordFormData) => {
    const returnReasonObj = reasons?.data.find((reason) => reason.name === data.returnReason);
    const finalizeData = {
      ...data,
      reason: data.returnReason,
      returnMailReasonId: returnReasonObj?.id,
    };
    if (selectedRecord) {
      updateRecord({ id: String(selectedRecord.id), data: finalizeData });
    }
  };

  const { mutate: updateRecord, isLoading: isUpdateLoading } = useMutation(
    ({ id, data }: { id: string; data: CreateRecordFormData }) => {
      return editUserAddress(id, data);
    },
    {
      onSuccess: async (response) => {
        toast.success('Successfully updated record');
        await queryClient.invalidateQueries(['userAddresses']);
        closeModal();
      },
      onError: async (error: Error) => {
        toast.error(error.message);
      },
    },
  );

  return (
    <dialog className="modal" id="edit_record_modal">
      <div className="modal-box w-full max-w-6xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div>
          <div className="text text-primary text-2xl">Edit Record</div>
          <div className="divider divider-primary m-0"></div>
        </div>

        {/* Form - Scrollable Content */}
        <form id="edit-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6 overflow-y-auto flex-1 px-4">
          {/* Identity */}
          <div className="rounded-box border border-base-300 p-4">
            <div className="text-xl font-semibold text-primary">Identity</div>
            <div className="divider divider-primary my-2"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <FormInput
                label="First Name"
                name="firstName"
                type="text"
                disabled={!isEditable}
                register={register('firstName', { required: 'First Name is required' })}
                errors={errors.firstName}
                touchedField={touchedFields.firstName}
                placeholder="first name"
                required={true}
              />
              <FormInput
                label="Last Name"
                name="lastName"
                type="text"
                disabled={!isEditable}
                register={register('lastName', { required: 'Last Name is required' })}
                errors={errors.lastName}
                touchedField={touchedFields.lastName}
                placeholder="last name"
                required={true}
              />
              <FormInput
                label="Company Name"
                name="companyName"
                type="text"
                disabled={!isEditable}
                register={register('companyName')}
                errors={errors.companyName}
                touchedField={touchedFields.companyName}
                placeholder="company name"
                required={false}
              />
              <FormInput
                label="Created By"
                name="createdByApimUsername"
                type="text"
                disabled={true}
                register={register('createdByApimUsername')}
                errors={errors.createdByApimUsername}
                touchedField={touchedFields.createdByApimUsername}
              />
              <FormInput
                label="Modified By"
                name="modifiedByApimUsername"
                type="text"
                disabled={true}
                register={register('modifiedByApimUsername')}
                errors={errors.modifiedByApimUsername}
                touchedField={touchedFields.modifiedByApimUsername}
              />
            </div>
          </div>
          {/* Address */}
          <div className="rounded-box border border-base-300 p-4">
            <div className="text-xl text-primary font-semibold ">Address</div>
            <div className="divider divider-primary my-2"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <FormInput
                label="Address Line 1"
                name="addressLine1"
                type="text"
                disabled={!isEditable}
                register={register('addressLine1', { required: 'Address Line 1 is required' })}
                errors={errors.addressLine1}
                touchedField={touchedFields.addressLine1}
                placeholder="address line 1"
                required={true}
              />
              <FormInput
                label="Address Line 2"
                name="addressLine2"
                type="text"
                disabled={!isEditable}
                register={register('addressLine2')}
                errors={errors.addressLine2}
                touchedField={touchedFields.addressLine2}
                placeholder="address line 2"
                required={false}
              />
              <FormInput
                label="City"
                name="city"
                type="text"
                disabled={!isEditable}
                register={register('city', { required: 'City is required' })}
                errors={errors.city}
                touchedField={touchedFields.city}
                placeholder="city"
                required={true}
              />
              <FormInput
                label="State"
                name="state"
                type="text"
                disabled={!isEditable}
                register={register('state', { required: 'State is required' })}
                errors={errors.state}
                touchedField={touchedFields.state}
                placeholder="state"
                required={true}
              />
              <FormInput
                label="Zip"
                name="zip"
                type="text"
                disabled={!isEditable}
                register={register('zip', { required: 'Zip is required' })}
                errors={errors.zip}
                touchedField={touchedFields.zip}
                placeholder="zip"
                required={true}
              />
              <FormInput
                label="Country"
                name="country"
                type="text"
                disabled={!isEditable}
                register={register('country', { required: 'Country is required' })}
                errors={errors.country}
                touchedField={touchedFields.country}
                placeholder="country"
                required={true}
              />
            </div>
          </div>
          {/* Document */}
          <div className="rounded-box border border-base-300 p-4">
            <div className="text-xl text-primary font-semibold ">Document</div>
            <div className="divider divider-primary my-2"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <FormInput
                label="Document Type"
                name="documentType"
                type="text"
                disabled={!isEditable}
                register={register('documentType')}
                errors={errors.documentType}
                touchedField={touchedFields.documentType}
                placeholder="document type"
                required={false}
              />
              <FormInput
                label="File Name"
                name="fileName"
                type="text"
                disabled={!isEditable}
                register={register('fileName')}
                errors={errors.fileName}
                touchedField={touchedFields.fileName}
                placeholder="file name"
                required={false}
              />
              <FormInput
                label="Barcode Digits"
                name="intelligentMailBarcodeDigits"
                type="text"
                disabled={!isEditable}
                register={register('intelligentMailBarcodeDigits')}
                errors={errors.intelligentMailBarcodeDigits}
                touchedField={touchedFields.intelligentMailBarcodeDigits}
                placeholder="barcode digits"
                required={false}
              />
            </div>
          </div>
          {/* Mail Status */}
          <div className="rounded-box border border-base-300 p-4">
            <div className="text-xl text-primary  font-semibold ">Mail Status</div>
            <div className="divider divider-primary my-2"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <FormInput
                label="Mailed"
                name="mailed"
                type="checkbox"
                disabled={!isEditable}
                register={register('mailed')}
                errors={errors.mailed}
                touchedField={touchedFields.mailed}
              />
              <FormInput
                label="Mailed Date"
                name="mailedDate"
                type="datetime-local"
                disabled={!isEditable}
                register={register('mailedDate')}
                errors={errors.mailedDate}
                touchedField={touchedFields.mailedDate}
                placeholder="mailed date"
                required={false}
              />
              <FormInput
                label="Return Date"
                name="returnDate"
                type="datetime-local"
                register={register('returnDate')}
                errors={errors.returnDate}
                touchedField={touchedFields.returnDate}
                placeholder="return date"
                required={false}
              />

              <FormSelect
                label="Return Reason"
                name="returnReason"
                register={register('returnReason')}
                errors={errors.returnReason}
                touchedField={touchedFields.returnReason}
                placeholder="Select return reason"
                list={reasons?.data ?? []}
              />

              <FormInput
                label="Logic App Last Run Id"
                name="logicAppLastRunId"
                type="text"
                disabled={!isEditable}
                register={register('logicAppLastRunId')}
                errors={errors.logicAppLastRunId}
                touchedField={touchedFields.logicAppLastRunId}
                placeholder="logic app last run id"
                required={false}
              />
            </div>
          </div>
          {/* Delivery */}
          <div className="rounded-box border border-base-300 p-4">
            <div className="text-xl text-primary font-semibold ">Delivery</div>
            <div className="divider divider-primary my-2"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <FormInput
                label="Delivery Address"
                name="deliveryAddress"
                type="text"
                disabled={!isEditable}
                register={register('deliveryAddress')}
                errors={errors.deliveryAddress}
                touchedField={touchedFields.deliveryAddress}
                placeholder="delivery address"
                required={false}
              />
              <FormInput
                label="Delivery City"
                name="deliveryCity"
                type="text"
                disabled={!isEditable}
                register={register('deliveryCity')}
                errors={errors.deliveryCity}
                touchedField={touchedFields.deliveryCity}
                placeholder="delivery city"
                required={false}
              />
              <FormInput
                label="Delivery State"
                name="deliveryState"
                type="text"
                disabled={!isEditable}
                register={register('deliveryState')}
                errors={errors.deliveryState}
                touchedField={touchedFields.deliveryState}
                placeholder="delivery state"
                required={false}
              />
              <FormInput
                label="Delivery Zip"
                name="deliveryZip"
                type="text"
                disabled={!isEditable}
                register={register('deliveryZip')}
                errors={errors.deliveryZip}
                touchedField={touchedFields.deliveryZip}
                placeholder="delivery zip"
                required={false}
              />
              <FormInput
                label="Alternate Address"
                name="alternateAddress"
                type="text"
                disabled={!isEditable}
                register={register('alternateAddress')}
                errors={errors.alternateAddress}
                touchedField={touchedFields.alternateAddress}
                placeholder="alternate address"
                required={false}
              />
            </div>
          </div>
          {/* Flags */}
          <div className="rounded-box border border-base-300 p-4">
            <div className="text-xl text-primary font-semibold ">Flags</div>
            <div className="divider divider-primary my-2"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <FormInput
                label="Is Duplicate"
                name="isDuplicate"
                type="checkbox"
                disabled={!isEditable}
                register={register('isDuplicate')}
                errors={errors.isDuplicate}
                touchedField={touchedFields.isDuplicate}
              />
              <FormInput
                label="Is Processed"
                name="isProcessed"
                type="checkbox"
                disabled={!isEditable}
                register={register('isProcessed')}
                errors={errors.isProcessed}
                touchedField={touchedFields.isProcessed}
              />
            </div>
          </div>
        </form>

        {/* Fixed Action Buttons */}
        <div className="modal-action border-t border-base-300 mt-6 pt-4">
          <button
            type="submit"
            form="edit-form"
            className="btn btn-success"
            disabled={isUpdateLoading || (!isValid && isEditable)}>
            <div>Update</div>
          </button>
          <button className="btn btn-error" onClick={closeModal}>
            Cancel
          </button>
        </div>

        <div className="fixed inset-0 pointer-events-none">
          <div className="pointer-events-auto">
            <ToastContainer containerId={'modal-plant'} position="top-center" />
          </div>
        </div>
      </div>

      <form method="dialog" className="modal-backdrop absolute w-full h-full" onClick={closeModal}>
        <button>close</button>
      </form>
    </dialog>
  );
};
